import { Modal, Flex, theme, Typography, Divider, Button, Select, Popover, Input, Space, InputRef, Spin } from 'antd'
import { useMoneyFlowLayoutModal } from "./useMoneyflowLayoutModal.ts"
import { Checkbox } from 'antd'
import { Fragment, useRef, useState } from "react"
import { Panels } from "../../utils/constants.ts"
import { useLayouts } from "../../hooks/useLayouts.ts"
import { DeleteOutlined, UndoOutlined } from "@ant-design/icons"

export const MoneyflowLayoutModal = () => {
    const [open, setOpen] = useState(false)
    const [newLayoutName, setNewLayoutName] = useState('')
    const nameInputRef = useRef<InputRef>(null)
    const { visible, hide } = useMoneyFlowLayoutModal()
    const { token } = theme.useToken()
    const {
        togglePanel,
        layout,
        setActiveLayout,
        layoutConfig: { layouts, activeLayout },
        addLayout,
        updateLayout,
        removeLayout,
        resetLayout,
        isFetching,
        isPending
    } = useLayouts()

    const handlePanelToggle = (item: Panels) => {
        togglePanel(item)
    }

    const handleNewLayoutNameSubmit = async () => {
        await addLayout(newLayoutName, layout.config)
        setNewLayoutName('')
        setOpen(false)
    }

    const handlePopoverChange = (newOpen: boolean) => {
        setOpen(newOpen)
    }

    const handleUpdateLayout = () => {
        updateLayout(activeLayout, layout.config)
    }

    const handleRemoveLayout = async () => {
        const ok = confirm('Are you sure you want to delete this layout?')
        if (!ok) return
        removeLayout(activeLayout)
    }

    const handleResetLayout = async () => {
        const ok = confirm('Are you sure you want to undo all changes?')
        if (!ok) return
        resetLayout()
    }

    return (
        <Modal
            title={
                <Typography.Title style={{ margin: 0, paddingBottom: token.paddingSM }} level={5}>
                    Layout
                </Typography.Title>
            }
            width={280}
            open={visible}
            onCancel={() => hide()}
            footer={null}
            mask={false}
            destroyOnClose
            style={{ margin: '0 0 0 auto', right: 24, top: 72 }}
        >
            <Spin spinning={isFetching || isPending}>
                <Flex vertical gap={token.marginXS} style={{ marginBlockEnd: token.margin, overflow: "hidden" }}>
                    <Select
                        value={activeLayout}
                        onChange={(value) => setActiveLayout(value)}
                        options={layouts.map(({ name }) => ({ value: name, label: name }))}
                    />
                    <Flex gap={4}>
                        <Button style={{ flexGrow: 1 }} onClick={handleUpdateLayout}
                            disabled={activeLayout === 'Default'}>
                            Save current layout
                        </Button>
                        <Button onClick={handleResetLayout} icon={<UndoOutlined/>}/>
                        <Button danger onClick={handleRemoveLayout} disabled={activeLayout === 'Default'}
                            icon={<DeleteOutlined/>}/>
                    </Flex>
                    <Divider/>
                    <Popover
                        content={<Space>
                            <Input
                                ref={nameInputRef}
                                value={newLayoutName}
                                onChange={(e) => setNewLayoutName(e.target.value)}
                            />
                            <Button type="primary" onClick={handleNewLayoutNameSubmit}>Create</Button>
                            <Button onClick={() => setOpen(false)}>Cancel</Button>
                        </Space>
                        }
                        title="New layout name"
                        trigger="click"
                        open={open}
                        onOpenChange={handlePopoverChange}
                        onPopupAlign={() => nameInputRef.current?.focus()}
                    >
                        <Button>Create new layout</Button>
                    </Popover>
                </Flex>
                <Divider style={{ marginBlockEnd: token.margin }}/>
                <Flex vertical gap={token.marginXS} style={{ marginBlockEnd: token.margin }}>
                    <Typography.Title style={{ margin: 0 }} level={5}>
                        Panels
                    </Typography.Title>
                    {Object.values(Panels).map((item) => (
                        <Fragment key={item}>
                            <Checkbox
                                style={{ padding: `${token.padding}px 0` }}
                                key={item}
                                value={item}
                                defaultChecked={!!layout.config.find((l) => l.i === item)}
                                checked={!!layout.config.find((l) => l.i === item)}
                                onChange={() => handlePanelToggle(item)}>
                                {item}
                            </Checkbox>
                            <Divider/>
                        </Fragment>
                    ))}
                </Flex>
            </Spin>
        </Modal>
    )
}
